import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSelectedTake3D } from "../../../app/slices/TrainingSlice";
import { IEmotionBubble } from "../../../models/interactions/EmotionBubble";
import "./EmotionBubblePreviewBody.css";
import MessageBubbleActorDropdown from "../message_bubble/MessageBubbleActorDropdown";
import MessageBubbleAnimationDropdown from "../message_bubble/MessageBubbleAnimationDropdown";
import EmotionBubblePreviewContent from "./EmotionBubblePreviewContent";
import ActorPropDropdown from "../lookat/ActorPropDropdown";
import usePropHelper from "../../../hooks/PropHelper";

type EmotionBubblePreviewBodyVariant = "standalone" | "embedded";

export default function EmotionBubblePreviewBody(props: {
    emotionBubble: IEmotionBubble;
    updateInteraction: (field: string, value: any, patch?: Partial<IEmotionBubble>) => void;
    variant?: EmotionBubblePreviewBodyVariant;
}) {

    const { emotionBubble, updateInteraction, variant = "embedded" } = props;
    const take = useSelector(selectSelectedTake3D);
    const { getBubbleTargetProps } = usePropHelper();
    const propsInTake = take
        ? getBubbleTargetProps(take, {
            kind: "timeline",
            interactionId: emotionBubble.id,
        })
        : [];
    const isPropIdInTake = (objectId: string) =>
        propsInTake.some((prop) => prop.id === objectId);
    const isPropAnchor = isPropIdInTake(emotionBubble.npcId);
    const handleAnchorChange = (objectId: string) => {
        if (isPropIdInTake(objectId)) {
            updateInteraction("npcId", objectId, { lookAt: "" });
            return;
        }

        updateInteraction("npcId", objectId);
    };

    useEffect(() => {
        if (!isPropAnchor || !emotionBubble.lookAt) return;
        updateInteraction("lookAt", "");
    }, [emotionBubble.lookAt, isPropAnchor, updateInteraction]);

    if (variant === "embedded") {
        return (
            <div className="emotionbubblepreviewbody-root">
                <div className="emotionbubblepreviewbody-controls">
                    <div className="emotionbubblepreviewbody-actorColumn">
                        <ActorPropDropdown label={"Actor"} selectedObjectId={emotionBubble.npcId} onClick={handleAnchorChange} availableProps={propsInTake} />
                        {isPropAnchor ? (
                            <DisabledLookAtField label="Look At" />
                        ) : (
                            <MessageBubbleActorDropdown label={"Look At"} selectedTargetActorId={emotionBubble.lookAt} onClick={(actorId) => updateInteraction("lookAt", actorId)} allowNoActor />
                        )}
                    </div>
                    <div className="emotionbubblepreviewbody-animationColumn">
                        <SettingsSection label="Animation">
                            <MessageBubbleAnimationDropdown selectedAnimation={emotionBubble.animation} onClick={(animation) => updateInteraction("animation", animation)} variant="preview" />
                        </SettingsSection>
                    </div>
                </div>

                <div className={"w-full flex-1 min-h-0"}>
                    <EmotionBubblePreviewContent emotionBubble={emotionBubble} onChange={(emotion) => updateInteraction("messageBubbleStyle", emotion)} />
                </div>
            </div>
        );
    }

    return (
        <div
            className="w-full h-full flex flex-col gap-4 rounded-xl overflow-hidden border"
            style={{
                borderColor: "var(--pi-game-blue-faded)",
                backgroundColor: "var(--pi-surface)",
            }}
        >
            <div className="grid grid-cols-1 piLg:grid-cols-2 gap-4 px-5 pt-5">
                <SettingsSection label="Actor">
                    <ActorPropDropdown
                        label={""}
                        selectedObjectId={emotionBubble.npcId}
                        onClick={handleAnchorChange}
                        availableProps={propsInTake}
                    />
                </SettingsSection>
                <SettingsSection label="Looks at">
                    {isPropAnchor ? (
                        <DisabledLookAtField label="" />
                    ) : (
                        <MessageBubbleActorDropdown
                            label={""}
                            selectedTargetActorId={emotionBubble.lookAt}
                            onClick={(actorId) => updateInteraction("lookAt", actorId)}
                            allowNoActor
                        />
                    )}
                </SettingsSection>
            </div>

            <div className="flex-1 min-h-0 grid grid-cols-1 piLg:grid-cols-[minmax(0,1fr)_minmax(320px,440px)] px-5 pb-5 gap-4">
                <SettingsSection label="Emotion">
                    <section
                        className="relative min-h-0 flex-1 rounded-lg border flex flex-col overflow-hidden"
                        style={{
                            borderColor: "var(--pi-game-blue-faded)",
                            backgroundColor: "var(--pi-game-blue-faded)",
                        }}
                    >
                        <div className="flex-1 min-h-0 flex items-center justify-center px-4 py-4">
                            <div className="w-full">
                                <EmotionBubblePreviewContent
                                    emotionBubble={emotionBubble}
                                    onChange={(emotion) => updateInteraction("messageBubbleStyle", emotion)}
                                    variant="standalone"
                                />
                            </div>
                        </div>
                    </section>
                </SettingsSection>

                <SettingsSection label="Animation">
                    <section
                        className="min-h-0 flex-1 rounded-lg border flex flex-col overflow-hidden"
                        style={{
                            borderColor: "var(--pi-game-blue-faded)",
                            backgroundColor: "var(--pi-game-blue-faded)",
                        }}
                    >
                        <div className="flex-1 min-h-0 flex flex-col px-4 py-4">
                            <MessageBubbleAnimationDropdown
                                selectedAnimation={emotionBubble.animation}
                                onClick={(animation) => updateInteraction("animation", animation)}
                                variant="standalone"
                            />
                        </div>
                    </section>
                </SettingsSection>
            </div>
        </div>
    );
}

function DisabledLookAtField(props: { label: string }) {
    return (
        <div className="w-full h-[98px] opacity-60">
            {props.label && (
                <div className="text-base font-bold text-interaction-primary h-[30px]">
                    {props.label}
                </div>
            )}
            <div className="flex p-4 w-full h-[68px] align-middle border-b-2 border-solid bg-interaction-softBackground border-interaction-primary items-center cursor-not-allowed">
                <div className="w-full font-medium text-interaction-primary text-interaction text-interaction-base">
                    No actor
                </div>
            </div>
        </div>
    );
}

function SettingsSection(props: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-2 min-w-0 min-h-0">
            {props.label && (
                <div className="text-base font-bold text-interaction-primary">
                    {props.label}
                </div>
            )}
            {props.children}
        </div>
    );
}
